import { DOCK_APPS } from "./OsAppRegistry";
import {
  SALTUS_DOCK_APP_IDS,
  isSaltusCompany,
  orderSaltusDockAppIds,
} from "./saltusDockApps";

export const OPENWORK_COMPANY_ID = "openworks";

/** Dock order after Home (Home is the leading special button). */
export const OPENWORK_DOCK_APP_IDS = [
  "clients",
  "artifacts",
  "meetings",
  "minesweeper",
] as const;

/** Not shown on OpenWork demo dock or Apps grid (includes API-only hidden modules). */
export const OPENWORK_EXCLUDED_APP_IDS = new Set([
  "missions",
  "mission-control",
  "dashboard",
  "scenario-tracker",
  "scenario-tracker-newsflow",
  "scenario-tracker-directionality",
  "scenario-tracker-heatmap",
  "scenario-tracker-deep-dive",
  "scenario-tracker-metric-projections",
  "screener",
  "playground",
  "cash-flow",
  "reports",
  "report-builder",
  "documents",
  "pods",
  "workers",
  "pulse-v2",
  "ops",
  "case-checking",
]);

/** Shown in the Apps grid as greyed-out teasers — not openable in the OpenWork demo. */
export const OPENWORK_MARKETING_APP_TOOLTIP = "Disabled for this demo sandbox";

export const OPENWORK_MARKETING_APP_IDS = ["cash-flow", "case-checking"] as const;

export type OpenworkAppsPopoverEntry = {
  appId: string;
  marketing: boolean;
};

export function isOpenworkCompany(companyId?: string | null): boolean {
  return (companyId ?? "").toLowerCase() === OPENWORK_COMPANY_ID;
}

export function orderOpenworkDockAppIds(appIds: string[]): string[] {
  const set = new Set(appIds);
  return OPENWORK_DOCK_APP_IDS.filter((id) => set.has(id));
}

export function resolveDefaultDockAppIds(companyId?: string | null): readonly string[] {
  if (isOpenworkCompany(companyId)) return OPENWORK_DOCK_APP_IDS;
  if (isSaltusCompany(companyId)) return SALTUS_DOCK_APP_IDS;
  return DOCK_APPS;
}

export function resolveDockBaseAppIds(
  companyId: string | null | undefined,
  pinnedAppIds: string[] | undefined,
): string[] {
  if (isSaltusCompany(companyId)) {
    if (pinnedAppIds && pinnedAppIds.length > 0) {
      const ordered = orderSaltusDockAppIds(pinnedAppIds);
      if (ordered.length > 0) {
        return ordered;
      }
    }
    return [...SALTUS_DOCK_APP_IDS];
  }

  if (!isOpenworkCompany(companyId)) {
    return pinnedAppIds && pinnedAppIds.length > 0 ? pinnedAppIds : [...DOCK_APPS];
  }

  if (pinnedAppIds && pinnedAppIds.length > 0) {
    const filtered = pinnedAppIds.filter(
      (id) => OPENWORK_DOCK_APP_IDS.includes(id as (typeof OPENWORK_DOCK_APP_IDS)[number]),
    );
    if (filtered.length > 0) {
      return orderOpenworkDockAppIds(filtered);
    }
  }

  return [...OPENWORK_DOCK_APP_IDS];
}

export function resolveDockAppLabel(
  appId: string,
  defaultLabel: string,
  companyId?: string | null,
): string {
  if (isOpenworkCompany(companyId) && appId === "artifacts") {
    return "Letters";
  }
  return defaultLabel;
}

export function filterOpenworkExcludedAppIds(appIds: string[]): string[] {
  return appIds.filter((id) => !OPENWORK_EXCLUDED_APP_IDS.has(id));
}

/** Append non-clickable marketing teasers after permitted Apps grid entries. */
export function buildOpenworkAppsPopoverEntries(
  clickableAppIds: string[],
  excludeAppIds: string[] = [],
): OpenworkAppsPopoverEntry[] {
  const seen = new Set([...clickableAppIds, ...excludeAppIds]);
  const entries: OpenworkAppsPopoverEntry[] = clickableAppIds.map((appId) => ({
    appId,
    marketing: false,
  }));
  for (const appId of OPENWORK_MARKETING_APP_IDS) {
    if (seen.has(appId)) continue;
    entries.push({ appId, marketing: true });
  }
  return entries;
}
